import React from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';

const Contact = () => {
  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      details: 'khalid@packetpath.ca',
      link: 'mailto:khalid@packetpath.ca',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: '+1 (866) 777-4321',
      link: 'tel:+18667774321',
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: 'Edmonton, Alberta, Canada',
      link: 'https://maps.google.com/?q=Edmonton,Alberta,Canada',
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    },
  ];

  const businessHours = [
    { day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM' },
    { day: 'Saturday', hours: '10:00 AM - 4:00 PM' },
    { day: 'Sunday', hours: 'Closed' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Main Content */}
      <main className="pt-16 lg:pt-18">
        {/* Hero Section */}
        <section className="section-padding bg-gradient-tech text-white">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in">
            Get In Touch
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto animate-slide-up">
            Have a question or ready to start your next project? We'd love to hear from you. 
            Reach out to our team and let's discuss how we can help.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <a
                  key={info.title}
                  href={info.link}
                  target={info.icon === MapPin ? '_blank' : undefined}
                  rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                  className="card-hover group animate-slide-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="p-8 text-center">
                    <div className={`w-16 h-16 ${info.bgColor} rounded-xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className={`w-8 h-8 ${info.color}`} />
                    </div>
                    <h3 className="text-xl font-bold text-text-primary mb-3">
                      {info.title}
                    </h3>
                    <p className="text-text-secondary group-hover:text-primary transition-colors">
                      {info.details}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>

          {/* Business Hours & Additional Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Business Hours */}
            <div className="bg-surface rounded-2xl p-8 shadow-elevation-1">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center mr-4">
                  <Clock className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-text-primary">Business Hours</h3>
              </div>
              <div className="space-y-4">
                {businessHours.map((schedule) => (
                  <div key={schedule.day} className="flex justify-between items-center py-3 border-b border-border last:border-0">
                    <span className="text-text-primary font-medium">{schedule.day}</span>
                    <span className="text-text-secondary">{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Response */}
            <div className="bg-gradient-tech rounded-2xl p-8 text-white">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Quick Response</h3>
              </div>
              <p className="text-blue-100 mb-6 leading-relaxed">
                We typically respond to all inquiries within 24 hours during business days. 
                For urgent matters, please call us directly.
              </p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2 mr-3"></div>
                  <p className="text-blue-100 text-sm">Average response time: 2-4 hours</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2 mr-3"></div>
                  <p className="text-blue-100 text-sm">Available for emergency support</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2 mr-3"></div>
                  <p className="text-blue-100 text-sm">Multiple communication channels</p>
                </div>
              </div>
            </div>
          </div>

          {/* Map or Additional Section */}
          <div className="mt-16 bg-surface rounded-2xl p-12 text-center shadow-elevation-1">
            <h3 className="text-2xl md:text-3xl font-bold text-text-primary mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
              Let's turn your vision into reality. Contact us today for a free consultation 
              and discover how we can help transform your business with innovative technology solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:khalid@packetpath.ca"
                className="btn-primary inline-flex items-center justify-center"
              >
                <Mail className="w-5 h-5 mr-2" />
                Send Us an Email
              </a>
              <a
                href="tel:+18667774321"
                className="btn-secondary inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Us Now
              </a>
            </div>
          </div>
        </div>
      </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Contact;
